/** @module What a card really is: variant, language and condition before it is booked. */
import { Check, Languages } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/client";
import { variantLabel } from "@/lib/cards";
import { type Condition, CONDITIONS, LANGUAGES } from "@/lib/collection";
import { money } from "@/lib/format";

type Variant = components["schemas"]["CardVariantOut"];

export { CONDITIONS, type Condition };

export interface Choice {
  variantId: number | null;
  language: string;
  condition: Condition;
}

/** The variant a photo cannot show: normal before reverse before holo. */
export function defaultVariant(variants: Variant[], suggested: number | null): Variant | null {
  if (variants.length === 0) return null;
  const chosen = suggested ? variants.find((variant) => variant.id === suggested) : undefined;
  if (chosen) return chosen;
  return (
    [...variants].sort(
      (a, b) => a.rank - b.rank || a.variant_key.localeCompare(b.variant_key),
    )[0] ?? null
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-[6px]">
      <legend className="t-label pb-[6px]">{label}</legend>
      <div className="flex flex-wrap gap-[6px]">{children}</div>
    </fieldset>
  );
}

export function ResolveForm({
  variants,
  choice,
  locale,
  onChange,
  suggestedLanguage,
}: {
  variants: Variant[];
  choice: Choice;
  locale: string;
  onChange: (choice: Choice) => void;
  /** The language the photo seems to show; offered as a hint when it differs. */
  suggestedLanguage?: string | null;
}) {
  const { t } = useTranslation();
  const hint =
    suggestedLanguage && suggestedLanguage !== choice.language ? suggestedLanguage : null;

  return (
    <div className="flex flex-wrap gap-x-6 gap-y-3">
      {variants.length > 0 ? (
        <Group label={t("review.variant")}>
          {variants.map((variant) => {
            const price = money(variant.prices?.[0]?.value ?? null, locale);
            const on = choice.variantId === variant.id;
            return (
              <button
                key={variant.id}
                type="button"
                className="chip"
                aria-pressed={on}
                onClick={() => {
                  onChange({ ...choice, variantId: variant.id });
                }}
              >
                {on ? <Check /> : null}
                {variantLabel(variant.variant_key, (key, fallback) => t(key, fallback))}
                {price && variants.length > 1 ? (
                  <span className="num t-xs opacity-70">{price}</span>
                ) : null}
              </button>
            );
          })}
        </Group>
      ) : null}

      <Group label={t("language.label")}>
        {LANGUAGES.map((language) => (
          <button
            key={language}
            type="button"
            className="chip"
            aria-pressed={choice.language === language}
            onClick={() => {
              onChange({ ...choice, language });
            }}
          >
            {language.toUpperCase()}
          </button>
        ))}
        {hint ? (
          <button
            type="button"
            className="chip chip-suggest"
            onClick={() => {
              onChange({ ...choice, language: hint });
            }}
          >
            <Languages />
            {t("review.ocrLanguage", { language: hint.toUpperCase() })}
          </button>
        ) : null}
      </Group>

      <Group label={t("review.condition")}>
        {CONDITIONS.map((condition) => (
          <button
            key={condition}
            type="button"
            className="chip num"
            aria-pressed={choice.condition === condition}
            onClick={() => {
              onChange({ ...choice, condition });
            }}
          >
            {condition}
          </button>
        ))}
      </Group>
    </div>
  );
}
